import React, { useMemo, useState } from 'react';

const PERSONAL_EMAIL_DOMAINS = new Set([
  'gmail.com',
  'googlemail.com',
  'yahoo.com',
  'yahoo.co.uk',
  'hotmail.com',
  'outlook.com',
  'live.com',
  'msn.com',
  'icloud.com',
  'me.com',
  'aol.com',
  'proton.me',
  'protonmail.com',
  'zoho.com',
  'yandex.com',
  'mail.com',
  'gmx.com',
]);

const isCompanyEmail = (email) => {
  const normalized = (email || '').trim().toLowerCase();
  const parts = normalized.split('@');
  if (parts.length !== 2) return false;
  const domain = parts[1];
  return domain.includes('.') && !PERSONAL_EMAIL_DOMAINS.has(domain);
};

const ServiceLeadForm = ({
  serviceName,
  submitLabel = 'Submit Request',
  consultationOptions = [],
  apiEndpoint = '/api/send-lead',
  successRedirectPath = '',
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobilePhone: '',
    consultationType: '',
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const apiBaseUrl = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
  const resolvedApiEndpoint = apiEndpoint.startsWith('http') ? apiEndpoint : `${apiBaseUrl}${apiEndpoint}`;

  const emailError = useMemo(() => {
    if (!formData.email) return '';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) return 'Enter a valid email address.';
    if (!isCompanyEmail(formData.email)) return 'Use your company email. Personal email providers are not allowed.';
    return '';
  }, [formData.email]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!formData.name.trim()) nextErrors.name = 'Name is required.';
    if (!formData.mobilePhone.trim()) nextErrors.mobilePhone = 'Mobile phone is required.';
    if (!formData.email.trim()) nextErrors.email = 'Email is required.';
    if (emailError) nextErrors.email = emailError;
    if (consultationOptions.length > 0 && !formData.consultationType) {
      nextErrors.consultationType = 'Please select a consultation type.';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    setStatus('loading');
    setMessage('');

    try {
      const response = await fetch(resolvedApiEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          service_name: serviceName,
          name: formData.name.trim(),
          email: formData.email.trim(),
          mobile_phone: formData.mobilePhone.trim(),
          consultation_type: formData.consultationType || null,
        }),
      });

      if (!response.ok) {
        let backendError = `Request failed (${response.status} ${response.statusText || 'Error'}).`;
        try {
          const payload = await response.json();
          if (payload?.detail) {
            backendError = typeof payload.detail === 'string' ? payload.detail : JSON.stringify(payload.detail);
          }
        } catch (_) {
          // Ignore JSON parsing errors and keep status-based message
        }
        throw new Error(backendError);
      }

      setStatus('success');
      if (successRedirectPath) {
        const params = new URLSearchParams();
        if (formData.consultationType) {
          params.set('consultation_type', formData.consultationType);
        }
        params.set('service', serviceName);
        window.location.assign(`${successRedirectPath}?${params.toString()}`);
        return;
      }
      setMessage("Thanks. Your request was received and we'll reach out shortly.");
      setFormData({ name: '', email: '', mobilePhone: '', consultationType: '' });
      setErrors({});
    } catch (error) {
      setStatus('error');
      if (error?.message === 'Failed to fetch') {
        setMessage('Network/API connection failed. Check your API base URL and backend availability.');
        return;
      }
      setMessage(error?.message || 'Submission failed. Please try again in a moment.');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-gray-200 p-6 sm:p-8 shadow-lg">
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Request {serviceName}</h2>
      <p className="text-gray-600 mb-6">Share your details and we will contact you to discuss next steps.</p>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor={`${serviceName}-name`}>
            Name
          </label>
          <input
            id={`${serviceName}-name`}
            name="name"
            value={formData.name}
            onChange={handleChange}
            type="text"
            className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            placeholder="Your full name"
          />
          {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor={`${serviceName}-email`}>
            Company Email
          </label>
          <input
            id={`${serviceName}-email`}
            name="email"
            value={formData.email}
            onChange={handleChange}
            type="email"
            className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            placeholder="you@company.com"
          />
          {(errors.email || emailError) && <p className="mt-1 text-sm text-red-600">{errors.email || emailError}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor={`${serviceName}-mobile`}>
            Mobile Phone
          </label>
          <input
            id={`${serviceName}-mobile`}
            name="mobilePhone"
            value={formData.mobilePhone}
            onChange={handleChange}
            type="tel"
            className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            placeholder="+1 (555) 123-4567"
          />
          {errors.mobilePhone && <p className="mt-1 text-sm text-red-600">{errors.mobilePhone}</p>}
        </div>

        {consultationOptions.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor={`${serviceName}-consultation-type`}>
              Type of Consultation
            </label>
            <select
              id={`${serviceName}-consultation-type`}
              name="consultationType"
              value={formData.consultationType}
              onChange={handleChange}
              className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white"
            >
              <option value="">Select a consultation type</option>
              {consultationOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {errors.consultationType && <p className="mt-1 text-sm text-red-600">{errors.consultationType}</p>}
          </div>
        )}
      </div>

      <button
        type="submit"
        disabled={status === 'loading'}
        className="mt-6 w-full bg-black hover:bg-gray-900 text-white font-semibold py-3 px-5 rounded-lg transition-colors disabled:opacity-50"
      >
        {status === 'loading' ? 'Submitting...' : submitLabel}
      </button>

      {message && (
        <p className={`mt-4 text-sm ${status === 'success' ? 'text-green-700' : 'text-red-600'}`}>
          {message}
        </p>
      )}
    </form>
  );
};

export default ServiceLeadForm;
